import { useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { EmptyState, Icon } from '@ui-kit';
import { useSearchIndex } from '../hooks/useSearchIndex';
import { SEARCH_GROUP_ORDER, type SearchHit, type SearchSource } from '../data/searchIndex';

export interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

const GROUP_LABEL_KEY: Record<SearchSource, string> = {
  capability: 'shared.search.group.capability',
  // queue #667 §UAT-2026-07-09-R667 §R667-5: 선택 가이드 검색 소스 그룹 라벨(로컬 · BE 0)
  decision: 'shared.search.group.decision',
  // queue #664 §UAT-2026-07-09-R664 §R664-5: 레시피 검색 소스 그룹 라벨(로컬 · BE 0)
  recipe: 'shared.search.group.recipe',
  // queue #665 §UAT-2026-07-09-R665 §R665-5: 설정 속성 검색 소스 그룹 라벨(로컬 · BE 0)
  config: 'shared.search.group.config',
  // queue #666 §UAT-2026-07-09-R666 §R666-5: 알려진 함정 검색 소스 그룹 라벨(로컬 · BE 0)
  pitfall: 'shared.search.group.pitfall',
  biz: 'shared.search.group.biz',
  menu: 'shared.search.group.menu',
  standards: 'shared.search.group.standards',
  // queue #689 §UAT-2026-07-14-R689 §R689-1-⑤: 문서 본문(소비자 가이드) 섹션 검색 소스 그룹 라벨
  doc: 'shared.search.group.doc',
  // queue #693 §UAT-2026-07-14-R693 §R693-5: 모듈 스펙 본문 전문 검색 소스 그룹 라벨(BE 위임)
  spec: 'shared.search.group.spec',
  term: 'shared.search.group.term',
};

/**
 * #659 §UAT-2026-07-08-R659 §R659-3 — 전역 통합 검색 커맨드 팔레트.
 *
 * ui-kit 에 모달성 컴포넌트가 `FormDialog` 뿐이나 하단 취소/저장 버튼 푸터·화면 중앙
 * 정렬 구조가 부적합해(실측 §R659-0) 자체 오버레이를 구현하되, 오버레이 backdrop
 * 클릭 닫힘·rAF 포커스 패턴은 FormDialog 를 그대로 차용한다.
 *
 * 접근성: `role="dialog" aria-modal="true"` + 검색 인풋 `role="combobox"` + 결과
 * `role="listbox"`/`option`. 키보드: `↑`/`↓`(그룹 경계 넘어 순환) · `Enter`(이동) ·
 * `Esc`(닫기 — useCommandPalette 의 전역 Esc 리스너 + 로컬 핸들러 이중 방어).
 */
export function CommandPalette({ isOpen, onClose }: CommandPaletteProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const { groups, sourceLoading, sourceErrors, isEmpty } = useSearchIndex(isOpen, query);

  const flatOptions = useMemo(
    () => groups.flatMap((group) => group.hits.map((hit) => ({ source: group.source, hit }))),
    [groups]
  );

  // 오픈 시 질의 초기화 + rAF 로 DOM 렌더 후 포커스(FormDialog 패턴 차용)
  useEffect(() => {
    if (!isOpen) return undefined;
    setQuery('');
    setHighlightedIndex(0);
    const id = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [isOpen]);

  // 질의가 바뀌면 하이라이트를 첫 항목으로 리셋
  useEffect(() => {
    setHighlightedIndex(0);
  }, [query]);

  if (!isOpen) return null;

  function handleSelect(hit: SearchHit) {
    if (hit.disabled) return;
    onClose();
    navigate(hit.href);
  }

  function handleKeyDown(e: ReactKeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
      return;
    }
    if (flatOptions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((i) => (i + 1) % flatOptions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((i) => (i - 1 + flatOptions.length) % flatOptions.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = flatOptions[highlightedIndex];
      if (target) handleSelect(target.hit);
    }
  }

  const trimmedQuery = query.trim();
  const showHint = trimmedQuery.length === 0;
  const activeOption = flatOptions[highlightedIndex];
  const activeOptionId = activeOption
    ? `command-palette-option-${activeOption.source}-${activeOption.hit.id}`
    : undefined;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('shared.search.trigger')}
      data-testid="command-palette"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--color-overlay-backdrop)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: '10vh',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: '640px',
          margin: '0 var(--spacing-4)',
          background: 'var(--color-surface-background)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-lg)',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '70vh',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-2)',
            padding: 'var(--spacing-3) var(--spacing-4)',
            borderBottom: '1px solid var(--color-surface-border)',
          }}
        >
          <Icon id="search" size={18} style={{ color: 'var(--color-text-muted)', flexShrink: 0 }} />
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-expanded="true"
            aria-controls="command-palette-listbox"
            aria-autocomplete="list"
            aria-activedescendant={activeOptionId}
            autoComplete="off"
            spellCheck={false}
            value={query}
            placeholder={t('shared.search.placeholder')}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              fontSize: 'var(--font-size-base)',
              color: 'var(--color-text-primary)',
              fontFamily: 'var(--font-family-sans)',
            }}
          />
          <span
            aria-hidden="true"
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-text-muted)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-sm)',
              padding: '2px 6px',
            }}
          >
            Esc
          </span>
        </div>

        <div
          id="command-palette-listbox"
          role="listbox"
          aria-label={t('shared.search.placeholder')}
          style={{ overflowY: 'auto', padding: 'var(--spacing-2) 0' }}
        >
          {showHint && (
            <div
              style={{
                padding: 'var(--spacing-4)',
                textAlign: 'center',
                color: 'var(--color-text-muted)',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              {t('shared.search.hint')}
            </div>
          )}

          {!showHint && isEmpty && (
            <div style={{ padding: 'var(--spacing-4)' }}>
              <EmptyState title={t('shared.search.empty')} />
            </div>
          )}

          {!showHint &&
            SEARCH_GROUP_ORDER.map((source) => {
              const group = groups.find((g) => g.source === source);
              const loading = sourceLoading[source];
              const errored = sourceErrors[source];
              if (!group && !loading && !errored) return null;

              return (
                <div key={source} role="group" aria-label={t(GROUP_LABEL_KEY[source])}>
                  <div
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-4)',
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: 600,
                      color: 'var(--color-text-muted)',
                      textTransform: 'uppercase',
                    }}
                  >
                    {t(GROUP_LABEL_KEY[source])}
                  </div>

                  {loading && !group && (
                    <div
                      style={{
                        padding: '0 var(--spacing-4) var(--spacing-2)',
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-muted)',
                      }}
                    >
                      {t('shared.search.loading')}
                    </div>
                  )}

                  {group?.hits.map((hit) => {
                    const optionId = `command-palette-option-${hit.source}-${hit.id}`;
                    const flatIndex = flatOptions.findIndex(
                      (opt) => opt.source === hit.source && opt.hit.id === hit.id
                    );
                    const isHighlighted = flatIndex === highlightedIndex;
                    return (
                      <div
                        key={optionId}
                        id={optionId}
                        role="option"
                        aria-selected={isHighlighted}
                        aria-disabled={hit.disabled || undefined}
                        onMouseEnter={() => setHighlightedIndex(flatIndex)}
                        onClick={() => handleSelect(hit)}
                        style={{
                          padding: 'var(--spacing-2) var(--spacing-4)',
                          cursor: hit.disabled ? 'not-allowed' : 'pointer',
                          background: isHighlighted ? 'var(--color-surface-muted)' : 'transparent',
                          opacity: hit.disabled ? 0.5 : 1,
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)' }}>
                          {hit.title}
                        </span>
                        {hit.subtitle && (
                          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                            {hit.subtitle}
                          </span>
                        )}
                      </div>
                    );
                  })}

                  {errored && (
                    <div
                      role="alert"
                      style={{
                        padding: '0 var(--spacing-4) var(--spacing-2)',
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-danger)',
                      }}
                    >
                      {t('shared.search.sourceError')}
                    </div>
                  )}
                </div>
              );
            })}
        </div>

        <div
          style={{
            padding: 'var(--spacing-2) var(--spacing-4)',
            borderTop: '1px solid var(--color-surface-border)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-muted)',
          }}
        >
          {t('shared.search.hint')}
        </div>
      </div>
    </div>
  );
}
